// hooks/useChat.js - Enhanced with Analytics
import { useCallback, useRef, useEffect } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from '../firebase'
import { RETRY_ATTEMPTS, RETRY_DELAY, MAX_MESSAGES } from '../constants'
import analyticsService from '../services/analyticsService'

export const useChat = (
  chatState,
  setChatState,
  addMessage,
  loadConversations
) => {
  const retryTimeoutRef = useRef(null)
  const currentRequestId = useRef(null)

  // Track retry attempts
  const sendMessageWithRetry = useCallback(
    async (message, deviceName, conversationId, retryCount = 0) => {
      const requestId = Date.now().toString()
      currentRequestId.current = requestId

      try {
        const functions = getFunctions(app)
        const chatWithBot = httpsCallable(functions, 'chatWithBot')

        const result = await chatWithBot({
          message,
          deviceName,
          conversationId,
          timestamp: Date.now()
        })

        // Track successful API call
        if (retryCount > 0) {
          analyticsService.trackUserAction('chat_retry_success', {
            retry_count: retryCount,
            request_id: requestId
          })
        }

        return result.data
      } catch (error) {
        console.error(`Chat-Fehler (Versuch ${retryCount + 1}):`, error)

        // Track retry attempt
        analyticsService.trackError('chat_api_error', {
          attempt: retryCount + 1,
          max_attempts: RETRY_ATTEMPTS,
          error_code: error.code,
          error_message: error.message,
          request_id: requestId,
          severity: retryCount >= RETRY_ATTEMPTS ? 'high' : 'medium'
        })

        if (retryCount < RETRY_ATTEMPTS) {
          const delay = RETRY_DELAY * Math.pow(2, retryCount)

          // Track retry scheduled
          analyticsService.trackUserAction('chat_retry_scheduled', {
            retry_count: retryCount + 1,
            delay_ms: delay,
            request_id: requestId
          })

          return new Promise((resolve, reject) => {
            retryTimeoutRef.current = setTimeout(async () => {
              try {
                const result = await sendMessageWithRetry(
                  message,
                  deviceName,
                  conversationId,
                  retryCount + 1
                )
                resolve(result)
              } catch (retryError) {
                reject(retryError)
              }
            }, delay)
          })
        }

        throw error
      }
    },
    []
  )

  const sendMessage = useCallback(async () => {
    const { currentMessage, deviceName, conversationId } = chatState
    const startTime = Date.now()

    if (!currentMessage.trim() || !deviceName.trim()) {
      const error = 'Bitte eine Nachricht UND einen Gerätename eingeben.'
      setChatState(prev => ({ ...prev, error }))
      analyticsService.trackError('validation_error_send', {
        has_message: !!currentMessage.trim(),
        has_device: !!deviceName.trim(),
        severity: 'low'
      })
      return
    }

    const userMessage = {
      type: 'user',
      content: currentMessage.trim(),
      timestamp: new Date()
    }

    // Track message characteristics
    const messageMetadata = {
      message_length: currentMessage.length,
      word_count: currentMessage.split(/\s+/).length,
      has_error_code: /[EF]-?\d{1,5}/.test(currentMessage),
      device_name: deviceName
    }

    analyticsService.trackChatMessage(
      'user',
      currentMessage.length,
      messageMetadata
    )

    // NUR lokales Update - Backend speichert die User-Message bereits
    setChatState(prev => ({
      ...prev,
      messages: [...prev.messages.slice(-MAX_MESSAGES + 1), userMessage],
      currentMessage: '',
      isTyping: true,
      error: null
    }))

    try {
      const result = await sendMessageWithRetry(
        currentMessage.trim(),
        deviceName.trim(),
        conversationId
      )

      const endTime = Date.now()
      const responseTime = endTime - startTime

      // Track performance metrics
      const performanceData = {
        responseTime,
        searchResults: result.sources?.length || 0,
        ...(result.performance?.embeddingTime && {
          embeddingTime: result.performance.embeddingTime
        }),
        ...(result.performance?.llmTime && {
          llmTime: result.performance.llmTime
        }),
        ...(result.performance?.totalTime && {
          totalTime: result.performance.totalTime
        })
      }

      analyticsService.trackChatPerformance(performanceData)

      // Track response characteristics
      const botMessage = {
        type: 'bot',
        content: result.answer,
        timestamp: new Date(),
        sources: result.sources || [],
        queryAnalysis: result.queryAnalysis || {},
        performance: performanceData
      }

      analyticsService.trackChatMessage('bot', result.answer.length, {
        deviceName,
        responseTime,
        sourcesCount: result.sources?.length || 0,
        hasErrorCode: result.queryAnalysis?.detectedErrorCode,
        confidence: result.queryAnalysis?.confidence
      })

      // Track source quality
      if (result.sources && result.sources.length > 0) {
        const avgRelevance =
          result.sources.reduce((sum, s) => sum + (s.relevanceScore || 0), 0) /
          result.sources.length

        analyticsService.logEvent('sources_quality', {
          source_count: result.sources.length,
          avg_relevance: avgRelevance,
          has_technician_knowledge: result.sources.some(
            s => s.type === 'technician_knowledge'
          ),
          device_name: deviceName
        })
      }

      // NUR lokales Update - Backend hat bereits in Firestore gespeichert!
      setChatState(prev => ({
        ...prev,
        messages: [...prev.messages.slice(-MAX_MESSAGES + 1), botMessage],
        conversationId: result.conversationId || prev.conversationId,
        isTyping: false,
        performance: performanceData
      }))

      // ⭐ NEU: Refresh Conversations nach Bot Response
      if (loadConversations) {
        console.log('✨ Refreshing conversations after bot response')
        loadConversations()
      }
    } catch (error) {
      console.error('Finaler Chat-Fehler:', error)

      const errorDuration = Date.now() - startTime

      analyticsService.trackError('chat_final_error', {
        message: error.message,
        code: error.code,
        duration_ms: errorDuration,
        device_name: deviceName,
        query_length: currentMessage.length,
        severity: 'critical'
      })

      const errorMessage = {
        type: 'bot',
        content:
          'Entschuldigung, es gab einen Fehler beim Verarbeiten Ihrer Anfrage. Bitte versuchen Sie es in einem Moment erneut.',
        timestamp: new Date(),
        sources: [],
        queryAnalysis: {},
        isError: true
      }

      setChatState(prev => ({
        ...prev,
        messages: [...prev.messages.slice(-MAX_MESSAGES + 1), errorMessage],
        isTyping: false,
        error: 'Chat-Verbindung fehlgeschlagen. Versuchen Sie es erneut.'
      }))
    }
  }, [chatState, setChatState, sendMessageWithRetry, loadConversations])

  const resetChat = useCallback(() => {
    const messageCount = chatState.messages.length

    analyticsService.trackUserAction('chat_reset', {
      message_count: messageCount,
      device_name: chatState.deviceName,
      had_conversation_id: !!chatState.conversationId
    })

    setChatState(prev => ({
      ...prev,
      messages: [],
      conversationId: null,
      currentMessage: '',
      error: null,
      performance: null
    }))
  }, [chatState, setChatState])

  const handleKeyPress = useCallback(
    e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()

        analyticsService.trackUserAction('message_sent_via_enter')
        sendMessage()
      }
    },
    [sendMessage]
  )

  // NEU: Sync-Funktion für Conversation-Wechsel
  const syncMessagesFromConversation = useCallback(
    conversation => {
      if (!conversation) {
        console.log('🔄 No conversation to sync')
        return
      }

      console.log('🔄 Syncing messages from conversation:', conversation.id)
      console.log('📊 Messages to sync:', conversation.messages?.length || 0)

      setChatState(prev => ({
        ...prev,
        messages: (conversation.messages || []).map(msg => {
          // ✅ Unterstütze beide: 'type' (von Firestore) und 'role' (von In-Memory)
          const msgType =
            msg.type ||
            (msg.role === 'user'
              ? 'user'
              : msg.role === 'assistant'
              ? 'bot'
              : 'bot')

          return {
            type: msgType,
            content: msg.content,
            timestamp: msg.timestamp || new Date(),
            // Bot-Messages können zusätzliche Felder haben
            ...(msgType === 'bot' && {
              sources: msg.sources || [],
              queryAnalysis: msg.queryAnalysis || {},
              performance: msg.performance || null
            })
          }
        }),
        conversationId: conversation.id,
        deviceName: conversation.deviceName || prev.deviceName,
        error: null
      }))

      console.log('✅ Messages synced successfully')
    },
    [setChatState]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }

      // Track session summary if there were messages
      if (chatState.messages.length > 0) {
        analyticsService.trackEngagement('chat_session_end', {
          message_count: chatState.messages.length,
          device_name: chatState.deviceName
        })
      }
    }
  }, [])

  return {
    sendMessage,
    resetChat,
    handleKeyPress,
    syncMessagesFromConversation // NEU exportiert!
  }
}
